import { Card, Grid, useTheme } from '@mui/material'
import {
  AccountDoc,
  DocType,
  EventDoc,
  MasterLabelDoc,
  MasterLabelLabel,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import TFMItemsTitle from './TFMTitle'
import TFMEventsColumns from './TFMEventsColumns'
import { ComponentType, useEffect, useState } from 'react'
import { withObservables } from '@nozbe/watermelondb/react'
import ObserveService from 'services/db/Observe'
import { AddIcon } from 'assets/icons'
import { t } from 'i18next'
import { AppUIType, useAppUIType } from 'components/providers/AppUITypeProvider'

import { RemoveTempEvents, addTempEventToDate } from 'services/db/Queries/Event'
import dayjs from 'dayjs'
import { generateStartEndBasedOnTimeWithShift } from 'utils/date'
import i18n from 'i18n'
import { Button } from 'components/buttons/styled'
import useLocalStorage from 'components/LocalStorageHook'
import { LocalStorageKey } from 'types/services/localStorage'
import { WorkerMessage } from 'workers/groupcalRecurrenceWorker'
import Logger from 'services/Logger'
import Database from 'services/db/Database'
import { prepareTasksForUI } from 'utils/fc_mapping'
import { getDefaultTimezone } from 'index'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'
import LocalStorage from 'services/LocalStorage'

interface TFMEventsViewProps {
  items?: Model<EventDoc>[]
  type: DocType
  events?: Model<EventDoc>[]
  userSettings?: Model<UserSettingsDoc>
  masterLabel?: Model<MasterLabelDoc>
  account?: Model<AccountDoc>
}

export function TFMEventsView(props: TFMEventsViewProps) {
  const theme = useTheme()
  //triggers re-render once someday date is changed
  const [someDayDate, setSomedayDate] = useLocalStorage(
    LocalStorageKey.SOMEDAY_DATE,
    dayjs().tz('UTC').startOf('day').unix().toString()
  )
  const appUiTypeContext = useAppUIType()
  const handleAddItem = () => {
    const startEnd = generateStartEndBasedOnTimeWithShift(dayjs().unix())
    addTempTaskToDate(
      startEnd.start,
      props.userSettings,
      props.type,
      appUiTypeContext.selectedLabel
    )
  }

  const showingDocType =
    appUiTypeContext.uiType.value === AppUIType.tasks
      ? DocType.TASK
      : DocType.NOTE

  const [events, setEvents] = useState<EventDoc[]>([])

  useEffect(() => {
    // in case of label change, reset the events
    setEvents([])
  }, [appUiTypeContext.flushCount])

  useEffect(() => {
    // Create worker
    const worker = new Worker(
      new URL('../../workers/groupcalRecurrenceWorker.ts', import.meta.url)
    )

    // Handle messages from worker
    worker.onmessage = (event: MessageEvent) => {
      let eventsForUI = prepareTasksForUI(
        props.events?.filter(
          (event) =>
            !event.Recurrence ||
            (event.Recurrence && typeof event.Recurrence === 'string')
        ),
        someDayDate,
        LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ?? '',
        appUiTypeContext.selectedLabel,
        showingDocType
      )

      eventsForUI.push(...event.data)

      setEvents(eventsForUI)
    }

    worker.onerror = (error) => {
      Logger.red('Worker:Error', error)
    }

    const forWorkerEvents: EventDoc[] = (
      (props.events ?? []).filter(
        (event) =>
          event.Recurrence &&
          typeof event.Recurrence !== 'string' &&
          (!appUiTypeContext.selectedLabel ||
            (event.Label &&
              typeof event.Label !== 'string' &&
              event.Label.some(
                (ele) =>
                  ele.LabelText === appUiTypeContext.selectedLabel?.ID &&
                  event.Type === showingDocType
              )))
      ) ?? []
    ).map((event) => {
      const cleanDoc = Database.toServerObject(event)
      return {
        ...cleanDoc
      }
    })

    const workerMessage: WorkerMessage = {
      events: forWorkerEvents,
      type: showingDocType,
      label: appUiTypeContext.selectedLabel,
      defaultTz: getDefaultTimezone()
    }
    // Start the worker with 10 million iterations
    worker.postMessage(workerMessage)

    // Cleanup
    return () => {
      worker.terminate()
    }
  }, [props.events, appUiTypeContext.selectedLabel, showingDocType])

  const { currentEventId } = useEventPopover()

  return (
    <Grid
      height={'100%'}
      display={'flex'}
      flexDirection={'column'}
      position={'relative'}
    >
      <Card
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.card.primary,
          height: '100%',
          borderRadius: '20px',
          zIndex: currentEventId ? 1001 : 1,
          border: `1px solid ${theme.palette.border.primary}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0px 8px 24px rgba(0, 0, 0, 0.45)'
              : '0px 4px 15px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div
          style={{
            padding: '0rem 2rem',
            display: 'flex',
            height: '92.5px',
            alignItems: 'center'
          }}
        >
          <TFMItemsTitle type={props.type} />
        </div>
        <TFMEventsColumns
          userSettings={props.userSettings}
          account={props.account}
          type={props.type}
          events={events}
        />
      </Card>
      <div
        style={{
          position: 'absolute',
          zIndex: 10,

          left: i18n.dir() === 'ltr' ? 20 : undefined,
          right: i18n.dir() === 'ltr' ? undefined : 20,
          bottom: 20
        }}
        onClick={handleAddItem}
      >
        <Button
          id="add-task-button"
          sx={{ fontWeight: 400, color: theme.palette.button.text.primary }}
        >
          <AddIcon />
          <span>
            {props.type === DocType.TASK
              ? t('task', { count: 1 })
              : t('note', { count: 1 })}
          </span>
        </Button>
      </div>
    </Grid>
  )
}

const TFMEventsViewWithEvents = withObservables(['type'], (props: TFMEventsViewProps) => ({
  events: ObserveService.Events(props.type)
}))(TFMEventsView as ComponentType<unknown>)

const TFMEventsViewWithAccount = withObservables([], () => ({
  account: ObserveService.Account()
}))(TFMEventsViewWithEvents as any)

export default withObservables([], () => ({
  userSettings: ObserveService.UserSettings()
}))(TFMEventsViewWithAccount as any) as any

export function addTempTaskToDate(
  startTimeSeconds: number,
  userSettings: UserSettingsDoc | undefined,
  type: DocType,
  label?: MasterLabelLabel
) {
  RemoveTempEvents('Adding temp task').then(() =>
    addTempEventToDate(
      startTimeSeconds,
      dayjs.unix(startTimeSeconds).add(30, 'minute').unix(),
      '',
      false,
      type,
      label?.ID
    )
  )
}
